import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import ReusableTable from "../../common/table/CommonTable";
import CustomTextField from "../../common/textfield/CommonTextField";
import {
  addDepartment,
  deleteDepartment,
  fetchDepartments,
  updateDepartment,
} from "../../redux/department/departmentAction";

const DepartmentManagementForm = () => {
  const [departments, setDepartments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm();
  const watchStatus = watch("status", true);

  const departmentList = useSelector(
    (state) => state?.department?.fetchDepartments || {}
  );
  const departmentDelete = useSelector(
    (state) => state?.department?.deleteDepartment || {}
  );
  const departmentAdd = useSelector(
    (state) => state?.department?.addDepartment || {}
  );
  const departmentUpdate = useSelector(
    (state) => state?.department?.updateDepartment || {}
  );

  useEffect(() => {
    dispatch(fetchDepartments());
  }, [dispatch]);

  // Function to normalize department names for comparison
  const normalizeDepartmentName = (name) => {
    if (!name) return "";
    // Remove extra spaces, trim, and convert to lowercase for comparison
    return name.replace(/\s+/g, " ").trim().toLowerCase();
  };

  useEffect(() => {
    if (departmentList?.departments) {
      setDepartments(
        departmentList.departments.map((department, index) => ({
          id: department.id,
          serial_no: department.serial_number,
          department_name: department.department_name,
          block_status: department.status,
          normalizedName: normalizeDepartmentName(department.department_name), // Store normalized name
        }))
      );
    }
  }, [departmentList]);

  useEffect(() => {
    if (departmentAdd?.success) {
      setSnackbarMessage("Department added successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchDepartments());
    } else if (departmentAdd?.error) {
      setSnackbarMessage(departmentAdd.error || "Failed to add department");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [departmentAdd, dispatch]);

  useEffect(() => {
    if (departmentUpdate?.success) {
      setSnackbarMessage("Department updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchDepartments());
    } else if (departmentUpdate?.error) {
      setSnackbarMessage(
        departmentUpdate.error || "Failed to update department"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [departmentUpdate, dispatch]);

  useEffect(() => {
    if (departmentDelete?.success) {
      setSnackbarMessage("Department deleted successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchDepartments());
    } else if (departmentDelete?.error) {
      setSnackbarMessage(
        departmentDelete.error || "Failed to delete department"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [departmentDelete, dispatch]);

  useEffect(() => {
    if (openDialog && dialogMode === "edit" && selectedDepartment) {
      setValue("department_name", selectedDepartment.department_name || "");
      setValue(
        "status",
        selectedDepartment.block_status === 1 ||
          selectedDepartment.block_status === true
      );
    }
  }, [openDialog, dialogMode, selectedDepartment, setValue]);

  // Function to check if a department name already exists
  const departmentNameExists = (name, currentDepartmentId = null) => {
    const normalizedNewName = normalizeDepartmentName(name);

    return departments.some((department) => {
      // Skip comparing with itself during edit
      if (currentDepartmentId && department.id === currentDepartmentId) {
        return false;
      }

      const normalizedExistingName =
        department.normalizedName ||
        normalizeDepartmentName(department.department_name);
      return normalizedNewName === normalizedExistingName;
    });
  };

  const columns = [
    { id: "serial_no", label: "Serial No", sortable: true },
    { id: "department_name", label: "Department Name", sortable: true },
    {
      id: "block_status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? "Active" : "Inactive"}
          color={value === 1 ? "success" : "error"}
          variant="filled"
          size="small"
          sx={{
            borderRadius: "16px",
            fontWeight: "bold",
            minWidth: "80px",
          }}
        />
      ),
    },
  ];

  const getFormFields = () => {
    return [
      {
        name: "department_name",
        label: "Department Name",
        rules: { required: "Department Name is required" },
      },
    ];
  };

  const handleOpenDialog = (mode, department = null) => {
    setDialogMode(mode);
    setSelectedDepartment(department);

    if (mode === "edit" && department) {
      reset();
      setValue("department_name", department.department_name || "");
      setValue(
        "status",
        department.block_status === 1 || department.block_status === true
      );
    } else {
      reset({ department_name: "", status: true });
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const validateDepartmentName = (e) => {
    const value = e.target.value.replace(/\s+/g, " ").trim();

    if (!value) {
      return;
    }

    const isDuplicate = departmentNameExists(
      value,
      dialogMode === "edit" ? selectedDepartment?.id : null
    );

    if (isDuplicate) {
      e.target.setCustomValidity("Department name already exists");
    } else {
      e.target.setCustomValidity("");
    }
  };

  const onSubmit = (data) => {
    setIsLoading(true);

    // Properly normalize the department name
    const department_name = data.department_name
      ? data.department_name.replace(/\s+/g, " ").trim()
      : "";

    // Check if name is empty after normalization
    if (!department_name) {
      setSnackbarMessage("Department name cannot be empty");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsLoading(false);
      return;
    }

    // Check for duplicate department names
    const isDuplicate = departmentNameExists(
      department_name,
      dialogMode === "edit" ? selectedDepartment.id : null
    );

    if (isDuplicate) {
      setSnackbarMessage(
        "Department name already exists. Please use a different name."
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setIsLoading(false);
      return;
    }

    if (dialogMode === "add") {
      const formData = {
        department_name: department_name,
        status: 1,
      };
      dispatch(addDepartment(formData));
    } else if (dialogMode === "edit") {
      const formData = {
        department_name: department_name,
        id: selectedDepartment.id,
        status: watchStatus ? 1 : 2,
      };
   
      dispatch(updateDepartment({ id: selectedDepartment.id, ...formData }));
    }
    setIsLoading(false);
    handleCloseDialog();
  };

  const handleOpenDeleteDialog = (department) => {
    setDepartmentToDelete(department);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDepartmentToDelete(null);
  };

  const handleDeleteDepartment = () => {
    if (departmentToDelete) {
      dispatch(deleteDepartment(departmentToDelete.id));
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Department Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "bold",
            color: "white",
            bgcolor: "#2c90f7",
          }}
        >
          Add Department
        </Button>
      </Box>

      <ReusableTable
        columns={columns}
        data={departments}
        isLoading={isLoading}
        title="Department"
        onEditClick={(department) => handleOpenDialog("edit", department)}
        onDeleteClick={(department) => handleOpenDeleteDialog(department)}
        customEmptyMessage="No departments found. Add a new department to get started."
        headerColor="#000000"
        rowsPerPage={5}
        withActions={true}
      />
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#129bab",
            color: "white",
          }}
        >
          {dialogMode === "add" ? "Add New Department" : "Edit Department"}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Paper elevation={0} sx={{ p: 2 }}>
              {getFormFields().map((field) => (
                <CustomTextField
                  key={field.name}
                  label={field.label}
                  value={watch(field.name, "")}
                  onChange={(e) => setValue(field.name, e.target.value)}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]?.message}
                  fullWidth
                  margin="normal"
                  type={field.type || "text"}
                  inputProps={{
                    maxLength: 50,
                    onBlur: (e) => {
                      const normalizedValue = e.target.value
                        .replace(/\s+/g, " ")
                        .trim();
                      setValue(field.name, normalizedValue);
                      validateDepartmentName(e);
                    },
                  }}
                />
              ))}

              {dialogMode === "edit" && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={watchStatus}
                        onChange={(e) => setValue("status", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Box>
              )}
            </Paper>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#129bab",
              justifyContent: "center",
            }}
          >
            <CustomButton
              onClick={handleCloseDialog}
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
            />
            <CustomButton
              type="submit"
              text={dialogMode === "add" ? "Add" : "Update"}
              variant="contained"
              disableElevation
              size="small"
              startIcon={<AddIcon />}
              disabled={
                departmentAdd?.inProgress || departmentUpdate?.inProgress
              }
              loading={
                departmentAdd?.inProgress || departmentUpdate?.inProgress
              }
            />
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{ bgcolor: "#f44336", color: "white" }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ mt: 2 }}>
            Are you sure you want to delete the department "
            {departmentToDelete?.department_name
              ? departmentToDelete.department_name.replace(/\s+/g, " ").trim()
              : ""}
            "? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <CustomButton
            onClick={handleCloseDeleteDialog}
            type="button"
            text="Cancel"
            variant="outlined"
            disableElevation
            size="small"
          />
          <CustomButton
            onClick={handleDeleteDepartment}
            type="button"
            text="Delete"
            variant="contained"
            disableElevation
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default DepartmentManagementForm;
